import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { submissionsApi } from '@api/submissions'
import { useSubmissionStore } from '@store/submissionStore'
import { useAuthStore } from '@store/authStore'
import { toast } from 'react-toastify'

/**
 * Submissions Hook
 */
export const useSubmissions = () => {
    const queryClient = useQueryClient()
    const { filters } = useSubmissionStore()
    const { user } = useAuthStore()

    // Get submissions query
    const {
        data: submissionsData,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: ['submissions', filters],
        queryFn: () => submissionsApi.getAll({
            status: filters.status,
            archived: filters.archived,
            search: filters.search,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            page: filters.page,
            perPage: filters.perPage,
            exclude_end_users: true, // Only show public submissions, not end-user submissions
        }),
        keepPreviousData: true,
        staleTime: 5 * 1000, // 5 seconds
        refetchInterval: 5 * 1000, // Auto-refresh every 5 seconds
    })

    // Archive mutation
    const archiveMutation = useMutation({
        mutationFn: (id) => submissionsApi.archive(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['submissions'])
            queryClient.invalidateQueries(['stats'])
            toast.success('Submission archived')
        },
        onError: () => {
            toast.error('Failed to archive submission')
        },
    })

    // Restore mutation
    const restoreMutation = useMutation({
        mutationFn: (id) => submissionsApi.restore(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['submissions'])
            queryClient.invalidateQueries(['stats'])
            toast.success('Submission restored')
        },
        onError: () => {
            toast.error('Failed to restore submission')
        },
    })

    // Update status mutation
    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status }) => submissionsApi.updateStatus(id, status),
        onSuccess: () => {
            queryClient.invalidateQueries(['submissions'])
            queryClient.invalidateQueries(['stats'])
        },
        onError: () => {
            toast.error('Failed to update status')
        },
    })

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: (id) => submissionsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['submissions'])
            queryClient.invalidateQueries(['stats'])
            toast.success('Submission deleted')
        },
        onError: () => {
            toast.error('Failed to delete submission')
        },
    })

    return {
        submissions: submissionsData?.data || [],
        pagination: submissionsData?.pagination || { total: 0, page: 1, per_page: 20, total_pages: 1 },
        isLoading,
        isError,
        error,
        refetch,
        archive: archiveMutation.mutate,
        restore: restoreMutation.mutate,
        updateStatus: updateStatusMutation.mutate,
        deleteSubmission: deleteMutation.mutate,
        isArchiving: archiveMutation.isPending,
        isRestoring: restoreMutation.isPending,
    }
}

/**
 * Single Submission Hook
 */
export const useSubmission = (id) => {
    const queryClient = useQueryClient()

    const {
        data: submissionData,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ['submission', id],
        queryFn: () => submissionsApi.getById(id),
        enabled: !!id,
        staleTime: 30 * 1000,
    })

    return {
        submission: submissionData?.data || null,
        isLoading,
        isError,
        error,
        refetch: () => queryClient.invalidateQueries(['submission', id]),
    }
}

export default useSubmissions
